// ** React imports
import { useCallback, useEffect, useRef, useState } from 'react';

// ** Redux imports
import { useDispatch, useSelector } from 'react-redux';
// ** Third Party imports
import {
  testAudioInputDevice,
  testAudioOutputDevice,
  testMediaConnectionBitrate,
  AudioInputTest,
  AudioOutputTest,
  MediaConnectionBitrateTest,
} from '@twilio/rtc-diagnostics';

import { getNetworkDiagnosticTokenRequest } from '../../../../components/dialer/extra-actions';
import { RootState } from '../../../../store/root-reducer';
import { executeOnRequestStatus } from '../../../utils/execute-on-request-status';
import {
  resetGetNetworkDiagnosticTokenRequest,
  resetNetworkDiagnosticIceServers,
} from '../../../../components/dialer/dialer-slice';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeviceOption {
  key: string;
  label: string;
}

export interface UsePreCallTestReturn {
  // Speaker
  speakerDevices: DeviceOption[];
  selectedSpeakerKey: string | undefined;
  onSpeakerChange: (key: string) => void;
  onTestSpeaker: () => void;
  onStopSpeakerTest: () => void;
  speakerLevel: number;
  isSpeakerTesting: boolean;
  speakerError: string | undefined;

  // Microphone
  micDevices: DeviceOption[];
  selectedMicKey: string | undefined;
  onMicChange: (key: string) => void;
  onTestMic: () => void;
  onStopMicTest: () => void;
  micLevel: number;
  isMicTesting: boolean;
  micError: string | undefined;

  // Network
  networkLevel: number;
  networkStatus: string;

  // Loading
  areDevicesLoading: boolean;

  // Permission
  hasMicPermission: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function volumeToBars(volume: number): number {
  return Math.round((volume / 100) * 10);
}

function bitrateToBars(kbps: number): number {
  if (!kbps) return 0;
  const maxKbps = 10000;
  const bars = Math.round((kbps / maxKbps) * 9) + 1;
  return Math.max(1, Math.min(10, bars));
}

function barsToNetworkStatus(bars: number): string {
  if (bars <= 0) return 'No network';
  if (bars <= 3) return 'Poor';
  if (bars <= 6) return 'Average';
  if (bars <= 9) return 'Good';
  return 'Excellent';
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export default function usePreCallTest(show: boolean): UsePreCallTestReturn {
  const dispatch = useDispatch();

  // ── Redux State ─────────────────────────────────────────────────────────────
  const networkDiagnosticIceServers = useSelector(
    (state: RootState) => state.dialer.networkDiagnosticIceServers,
  );
  const getNetworkDiagnosticTokenRequestStatus = useSelector(
    (state: RootState) => state.dialer.getNetworkDiagnosticTokenRequest.status,
  );

  // ── Device Lists ────────────────────────────────────────────────────────────
  const [areDevicesLoading, setAreDevicesLoading] = useState(true);
  const [speakerDevices, setSpeakerDevices] = useState<DeviceOption[]>([]);
  const [micDevices, setMicDevices] = useState<DeviceOption[]>([]);
  const [selectedSpeakerKey, setSelectedSpeakerKey] = useState<
    string | undefined
  >(undefined);
  const [selectedMicKey, setSelectedMicKey] = useState<string | undefined>(
    undefined,
  );

  // ── Speaker State ───────────────────────────────────────────────────────────
  const [isSpeakerTesting, setIsSpeakerTesting] = useState(false);
  const [speakerLevel, setSpeakerLevel] = useState(0);
  const [speakerError, setSpeakerError] = useState<string | undefined>(
    undefined,
  );

  // ── Mic State ───────────────────────────────────────────────────────────────
  const [isMicTesting, setIsMicTesting] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [micError, setMicError] = useState<string | undefined>(undefined);
  const [hasMicPermission, setHasMicPermission] = useState(true);

  // ── Network State ───────────────────────────────────────────────────────────
  const [networkLevel, setNetworkLevel] = useState(0);
  const [networkStatus, setNetworkStatus] = useState('Checking...');

  // ── Test Instance Refs ──────────────────────────────────────────────────────
  // Stored as refs (not state) to avoid triggering re-renders on assignment
  const speakerTestRef = useRef<AudioOutputTest | null>(null);
  const micTestRef = useRef<AudioInputTest | null>(null);
  const networkTestRef = useRef<MediaConnectionBitrateTest | null>(null);

  // ── Stable refs for selected keys (used in callbacks without stale closures) ─
  const selectedSpeakerKeyRef = useRef(selectedSpeakerKey);
  const selectedMicKeyRef = useRef(selectedMicKey);
  useEffect(() => {
    selectedSpeakerKeyRef.current = selectedSpeakerKey;
  }, [selectedSpeakerKey]);
  useEffect(() => {
    selectedMicKeyRef.current = selectedMicKey;
  }, [selectedMicKey]);

  // ── Stable refs for device lists (used in callbacks without stale closures) ─
  const speakerDevicesRef = useRef(speakerDevices);
  const micDevicesRef = useRef(micDevices);
  useEffect(() => {
    speakerDevicesRef.current = speakerDevices;
  }, [speakerDevices]);
  useEffect(() => {
    micDevicesRef.current = micDevices;
  }, [micDevices]);

  // ── Stop All Tests ──────────────────────────────────────────────────────────

  const stopAllTests = useCallback(() => {
    if (speakerTestRef.current) {
      speakerTestRef.current.stop();
      speakerTestRef.current = null;
    }
    if (micTestRef.current) {
      micTestRef.current.stop();
      micTestRef.current = null;
    }
    if (networkTestRef.current) {
      networkTestRef.current.stop();
      networkTestRef.current = null;
    }
    setIsSpeakerTesting(false);
    setIsMicTesting(false);
    setSpeakerLevel(0);
    setMicLevel(0);
  }, []);

  // ── Device Enumeration ──────────────────────────────────────────────────────

  const enumerateDevices = useCallback(async () => {
    let micPermissionGranted = false;

    try {
      // getUserMedia required to unlock device labels in the browser
      const stream = await navigator?.mediaDevices?.getUserMedia({
        audio: true,
      });
      stream?.getTracks()?.forEach((track) => track?.stop());
      micPermissionGranted = true;
    } catch {
      setAreDevicesLoading(false);
      setHasMicPermission(false);
      setSpeakerError(
        'Speaker access denied. Please allow microphone permissions.',
      );
      setMicError(
        'Microphone access denied. Please allow microphone permissions.',
      );
    }

    // If mic permission was denied, leave both device lists empty
    if (!micPermissionGranted) return;

    try {
      const devices = await navigator?.mediaDevices?.enumerateDevices();

      const mics: DeviceOption[] = devices
        ?.filter((d) => d?.kind === 'audioinput')
        ?.map((d, i) => ({
          key: d?.deviceId,
          label: d?.label || `Microphone ${i + 1}`,
        }));

      const speakers: DeviceOption[] = devices
        ?.filter((d) => d?.kind === 'audiooutput')
        ?.map((d, i) => ({
          key: d?.deviceId,
          label: d?.label || `Speaker ${i + 1}`,
        }));

      setMicDevices(mics);
      setSpeakerDevices(speakers);

      if (mics?.length > 0 && !selectedMicKeyRef.current) {
        const defaultMic = mics.find((m) => m.key === 'default') || mics[0];
        setSelectedMicKey(defaultMic.key);
      }
      if (speakers.length > 0 && !selectedSpeakerKeyRef.current) {
        const defaultSpeaker =
          speakers.find((s) => s.key === 'default') || speakers[0];
        setSelectedSpeakerKey(defaultSpeaker.key);
      }
    } catch {
      // enumerateDevices API unavailable — both lists remain empty
      setAreDevicesLoading(false);
    }
    setAreDevicesLoading(false);
  }, []);

  // ── Network Test ────────────────────────────────────────────────────────────

  const startNetworkTest = useCallback(() => {
    if (networkTestRef.current) {
      networkTestRef.current.stop();
      networkTestRef.current = null;
    }

    setNetworkLevel(0);
    setNetworkStatus('Checking...');

    // Attach listeners synchronously — the test starts on next tick internally
    const test = testMediaConnectionBitrate({
      iceServers: networkDiagnosticIceServers as any,
    });

    test.on(MediaConnectionBitrateTest.Events.Bitrate, (kbps: number) => {
      const bars = bitrateToBars(kbps);
      setNetworkLevel(bars);
      setNetworkStatus(barsToNetworkStatus(bars));
    });

    test.on(MediaConnectionBitrateTest.Events.Error, () => {
      setNetworkLevel(0);
      setNetworkStatus('Unable to test');
      networkTestRef.current = null;
    });

    test.on(
      MediaConnectionBitrateTest.Events.End,
      (report: MediaConnectionBitrateTest.Report) => {
        const finalBars = bitrateToBars(report.averageBitrate);
        setNetworkLevel(finalBars);
        setNetworkStatus(barsToNetworkStatus(finalBars));
        networkTestRef.current = null;
      },
    );

    networkTestRef.current = test;
  }, [networkDiagnosticIceServers]);

  const resetNetworkTestOnModalClose = useCallback(() => {
    dispatch(resetNetworkDiagnosticIceServers());
    dispatch(resetGetNetworkDiagnosticTokenRequest());
    stopAllTests();
  }, [dispatch]);

  // ── Modal Open/Close Effect ─────────────────────────────────────────────────

  useEffect(() => {
    if (show) {
      setSpeakerError(undefined);
      setMicError(undefined);
      setSpeakerLevel(0);
      setMicLevel(0);
      setNetworkLevel(0);
      setNetworkStatus('Checking...');

      enumerateDevices();
      dispatch(getNetworkDiagnosticTokenRequest());
    } else {
      resetNetworkTestOnModalClose();
      stopAllTests();
    }

    return () => {
      stopAllTests();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // ── Network Test Status Effect ───────────────────────────────────────────────

  useEffect(() => {
    executeOnRequestStatus({
      status: getNetworkDiagnosticTokenRequestStatus,
      onSuccess: () => {
        startNetworkTest();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNetworkDiagnosticTokenRequestStatus]);

  // ── Speaker Test Handlers ───────────────────────────────────────────────────

  const onTestSpeaker = useCallback(() => {
    if (speakerDevicesRef.current.length === 0) {
      setSpeakerError(
        (prev) =>
          prev || 'No speaker device available. Check microphone permissions.',
      );
      return;
    }

    if (speakerTestRef.current) return;

    setSpeakerError(undefined);
    setSpeakerLevel(0);
    setIsSpeakerTesting(true);

    const deviceId =
      selectedSpeakerKeyRef.current &&
      selectedSpeakerKeyRef.current !== 'default'
        ? selectedSpeakerKeyRef.current
        : undefined;

    // Attach listeners synchronously — AudioOutputTest starts via setTimeout
    const test = testAudioOutputDevice({ deviceId, doLoop: true });

    test.on(AudioOutputTest.Events.Volume, (volume: number) => {
      setSpeakerLevel(volumeToBars(volume));
    });

    test.on(AudioOutputTest.Events.Error, () => {
      setSpeakerError('Unable to play audio. Check your speaker settings.');
      setIsSpeakerTesting(false);
      setSpeakerLevel(0);
      speakerTestRef.current = null;
    });

    test.on(AudioOutputTest.Events.End, () => {
      setIsSpeakerTesting(false);
      setSpeakerLevel(0);
      speakerTestRef.current = null;
    });

    speakerTestRef.current = test;
  }, []);

  const onStopSpeakerTest = useCallback(() => {
    if (speakerTestRef.current) {
      speakerTestRef.current.stop();
      speakerTestRef.current = null;
    }
    setIsSpeakerTesting(false);
    setSpeakerLevel(0);
  }, []);

  const onSpeakerChange = useCallback((key: string) => {
    if (speakerTestRef.current) {
      speakerTestRef.current.stop();
      speakerTestRef.current = null;
      setIsSpeakerTesting(false);
      setSpeakerLevel(0);
    }
    setSelectedSpeakerKey(key);
  }, []);

  // ── Mic Test Handlers ───────────────────────────────────────────────────────

  const onTestMic = useCallback(() => {
    if (micDevicesRef.current.length === 0) {
      setMicError((prev) => prev || 'No microphone device available.');
      return;
    }

    if (micTestRef.current) return;

    setMicError(undefined);
    setMicLevel(0);
    setIsMicTesting(true);

    const deviceId =
      selectedMicKeyRef.current && selectedMicKeyRef.current !== 'default'
        ? selectedMicKeyRef.current
        : undefined;

    // Attach listeners synchronously — AudioInputTest starts via setTimeout
    const test = testAudioInputDevice({ deviceId });

    test.on(AudioInputTest.Events.Volume, (volume: number) => {
      setMicLevel(volumeToBars(volume));
    });

    test.on(AudioInputTest.Events.Error, () => {
      setMicError(
        'Unable to access microphone. Please check your permissions.',
      );
      setIsMicTesting(false);
      setMicLevel(0);
      micTestRef.current = null;
    });

    test.on(AudioInputTest.Events.End, () => {
      setIsMicTesting(false);
      setMicLevel(0);
      micTestRef.current = null;
    });

    micTestRef.current = test;
  }, []);

  const onStopMicTest = useCallback(() => {
    if (micTestRef.current) {
      micTestRef.current.stop();
      micTestRef.current = null;
    }
    setIsMicTesting(false);
    setMicLevel(0);
  }, []);

  const onMicChange = useCallback((key: string) => {
    if (micTestRef.current) {
      micTestRef.current.stop();
      micTestRef.current = null;
      setIsMicTesting(false);
      setMicLevel(0);
    }
    setSelectedMicKey(key);
  }, []);

  // ── Return ──────────────────────────────────────────────────────────────────

  return {
    speakerDevices,
    selectedSpeakerKey,
    onSpeakerChange,
    onTestSpeaker,
    onStopSpeakerTest,
    speakerLevel,
    isSpeakerTesting,
    speakerError,

    micDevices,
    selectedMicKey,
    onMicChange,
    onTestMic,
    onStopMicTest,
    micLevel,
    isMicTesting,
    micError,

    networkLevel,
    networkStatus,

    areDevicesLoading,

    hasMicPermission,
  };
}
